import type { ActivePassiveSnapshot } from './UpgradeSystem';
import type { ActiveWeaponSnapshot } from './WeaponSystem';
import type { PlayerStatsConfig } from '../components/PlayerStats';

export type DebugCommandName = 'spawnEnemy' | 'giveWeapon' | 'skipWave' | 'forceWave' | 'getDebugStatsSnapshot';

export type DebugCommandSource = 'e2e' | 'hotkey:F6' | 'hotkey:F7' | 'hotkey:F8' | 'manual';

export type DebugCommandFailureReason =
  | 'testing-disabled'
  | 'main-scene-not-active'
  | 'scene-not-ready'
  | 'invalid-payload'
  | 'unknown-enemy'
  | 'unknown-weapon'
  | 'weapon-slot-full'
  | 'weapon-max-level'
  | 'waves-unloaded'
  | 'wave-index-out-of-range'
  | 'boss-pause-active'
  | 'blocked-by-overlay'
  | 'execution-failed';

export type DebugCommandResult<T> =
  | {
    readonly ok: true;
    readonly command: DebugCommandName;
    readonly source: DebugCommandSource;
    readonly data: T;
  }
  | {
    readonly ok: false;
    readonly command: DebugCommandName;
    readonly source: DebugCommandSource;
    readonly reason: DebugCommandFailureReason;
    readonly message?: string;
    readonly details?: unknown;
  };

export interface SpawnEnemyDebugPayload {
  readonly enemyId?: string;
  readonly x?: number;
  readonly y?: number;
  readonly source?: DebugCommandSource;
}

export interface GiveWeaponDebugPayload {
  readonly weaponId?: string;
  readonly targetLevel?: number;
  readonly source?: DebugCommandSource;
}

export interface SkipWaveDebugPayload {
  readonly source?: DebugCommandSource;
}

export interface ForceWaveDebugPayload {
  readonly waveIndex: number;
  readonly source?: DebugCommandSource;
}

export interface DebugStatsSnapshotPayload {
  readonly source?: DebugCommandSource;
}

export interface DebugInventorySnapshot {
  readonly activeWeaponCount: number;
  readonly maxWeapons: number;
  readonly activePassiveCount: number;
  readonly maxPassives: number;
  readonly weapons: ReadonlyArray<Pick<ActiveWeaponSnapshot, 'id' | 'level' | 'maxLevel'>>;
  readonly passives: ReadonlyArray<Pick<ActivePassiveSnapshot, 'id' | 'level' | 'maxLevel'>>;
}

export interface DebugWaveActionSnapshot {
  readonly action: 'skip' | 'force';
  readonly source: DebugCommandSource;
  readonly previousWaveIndex: number;
  readonly nextWaveIndex: number;
  readonly result: 'accepted' | 'blocked';
  readonly reason?: DebugCommandFailureReason;
  readonly timestampMs: number;
}

export interface SpawnEnemyDebugResult {
  readonly enemyId: string;
  readonly x: number;
  readonly y: number;
  readonly activeEnemyCount: number;
  readonly usedDefaultCoordinates: boolean;
}

export interface GiveWeaponDebugResult {
  readonly weaponId: string;
  readonly targetLevel: number;
  readonly currentLevel: number;
  readonly unlocked: boolean;
  readonly action: 'unlocked' | 'leveled' | 'already-owned';
  readonly inventory: DebugInventorySnapshot;
}

export interface WaveDebugResult {
  readonly previousWaveIndex: number;
  readonly nextWaveIndex: number;
  readonly elapsedWaveTime: number;
  readonly lastDebugWaveAction: DebugWaveActionSnapshot;
}

export interface DebugStatsSnapshot {
  readonly gameplayElapsedSeconds: number;
  readonly experience: {
    readonly level: number;
    readonly currentXp: number;
    readonly xpToNextLevel: number;
  };
  readonly playerStats: PlayerStatsConfig;
  readonly health: {
    readonly current: number;
    readonly max: number;
  };
  readonly inventory: DebugInventorySnapshot;
  readonly activeEnemyCount: number;
  readonly wave: {
    readonly currentWaveIndex: number;
    readonly elapsedWaveTime: number;
    readonly wavesStarted: number;
    readonly elapsedRunTime: number;
    readonly bossActive: boolean;
    readonly bossesSpawned: number;
    readonly pauseWaves: boolean;
  };
  readonly lastCommandResult?: DebugCommandResult<unknown>;
}

export function resolveDebugCommandSource(source: DebugCommandSource | undefined): DebugCommandSource {
  return source ?? 'manual';
}

export function createDebugCommandSuccess<T>(
  command: DebugCommandName,
  source: DebugCommandSource,
  data: T
): DebugCommandResult<T> {
  return {
    ok: true,
    command,
    source,
    data
  };
}

export function createDebugCommandFailure(
  command: DebugCommandName,
  source: DebugCommandSource,
  reason: DebugCommandFailureReason,
  message?: string,
  details?: unknown
): DebugCommandResult<never> {
  return {
    ok: false,
    command,
    source,
    reason,
    message,
    details
  };
}
